import { CommonModule } from '@angular/common';
import { Component, Input, NgModule, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { Observable } from 'rxjs';

import { HookResult, StateService, Transition, UiOnExit, UIRouterModule } from '@uirouter/angular';

import { TranslateService } from '@codeandweb/ngx-translate';

import { MenuModule } from '@app/primeng-overrides/menu';
import { ConfirmationService } from 'primeng/api';
import { DividerModule } from 'primeng/divider';
import { DialogService } from 'primeng/dynamicdialog';
import { PanelModule } from 'primeng/panel';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';

import { EventManagerService } from '@app/_global/event-manager.service';
import { Abonne } from '@app/abonne/abonne.model';
import { AbonneService } from '@app/abonne/abonne.service';
import { AuthService } from '@app/auth/auth.service';
import { CiviliteSelectorModule } from '@app/utilisateur/civilite-selector';
import { Utilisateur, UtilisateurAbonne } from '@app/utilisateur/utilisateur.model';
import { computeEmailingMessages, UtilisateurService } from '@app/utilisateur/utilisateur.service';
import { GlobalModule } from '@global/global.module';

import { simpleComparison } from '@app/_helpers/utils';

@Component({
	selector: 'utilisateur-general-tab',
	templateUrl: `utilisateur-general-tab.component.html`
})
export class UtilisateurGeneralTabComponent implements OnInit, UiOnExit {

	@Input() me: Utilisateur;
	@Input() utilisateur: Utilisateur;
	@Input() linkToAbonneAtCreation = false;
	@Input() isAdminView = false;

	pristineUtilisateur: Utilisateur;

	submitted = false;

	isCreation = false;
	canEditUser = false;
	editMode = false;
	selfEditing = false;
	isSubscriptionManagerView = false;
	reinitialisationMotDePasseLoading: boolean = false;
	loading = false;
	skipExitStateCheck = false;

	regulatoryEventEmailingMessage: string;
	weeklySummaryEmailingMessage: string;
	monthlySummaryEmailingMessage: string;

	// Vue du gestionnaire d'abonnement
	currentAbonne: Abonne | null;
	currentUtiAbonne: UtilisateurAbonne;

	constructor(
		private stateService: StateService,
		private abonneService: AbonneService,
		private utilisateurService: UtilisateurService,
		private eventManager: EventManagerService,
		private dialogService: DialogService,
		private confirmationService: ConfirmationService,
		private translationService: TranslateService,
		private authService: AuthService,
	) {
		this.currentAbonne = this.abonneService.currentAbonneValue!;
	}

	ngOnInit(): void {
		this.canEditUser = this.me.uti_administrateur;
		this.isCreation = this.utilisateur == undefined;

		let utiAbonne = this.currentAbonne?.utilisateurs_abonnes.find(utilisateur => utilisateur.uti_id == this.me.uti_id);
		if (utiAbonne && utiAbonne.utb_gestion_abonnement) {
			this.isSubscriptionManagerView = true;
			this.currentUtiAbonne = utiAbonne;
		}

		if (this.canEditUser) {
			if (this.isCreation) {
				this.utilisateur = {uti_actif: true, uti_bloque: false} as Utilisateur;
				this.editMode = true;
			}
			else if (this.utilisateur.uti_civilite && this.utilisateur.uti_civilite.trim() == '') {
				this.utilisateur.uti_civilite = '';
			}
		}

		this.pristineUtilisateur = structuredClone(this.utilisateur);
		this.selfEditing = this.me.uti_id == this.utilisateur.uti_id;

		if (! this.isCreation) {
			let {regulatoryEventEmailingMessage, weeklySummaryEmailingMessage, monthlySummaryEmailingMessage}
				= computeEmailingMessages(this.translationService, this.utilisateur);

			this.regulatoryEventEmailingMessage = regulatoryEventEmailingMessage;
			this.weeklySummaryEmailingMessage = weeklySummaryEmailingMessage;
			this.monthlySummaryEmailingMessage = monthlySummaryEmailingMessage;
		}
	}

	toggleEditMode(status?: boolean) {
		this.editMode = (status !== undefined)? status : !!!this.editMode;
	}

	submit(){
		this.submitted = true;

		if (this.isCreation) {
			let abo_id: number | undefined;
			if (this.linkToAbonneAtCreation && this.currentAbonne) {
				abo_id = this.currentAbonne.abo_id;
			}

			this.utilisateurService.createUtilisateur(this.utilisateur, abo_id)
			.subscribe({
				next: ({ uti_id }) => {
					this.eventManager.emit('toast', { severity: 'success', summary: 'Utilisateur créé' });

					this.skipExitStateCheck = true;

					this.abonneService.refreshCurrentAbonneData().subscribe(() => {
						this.stateService.go('^.single', {uti_id: uti_id});
					});
				}
			})
		}
		else {
			this.utilisateurService.updateUtilisateur(this.utilisateur, this.selfEditing)
			.subscribe({
				next: () => {
					this.eventManager.emit('toast', { severity: 'success', summary: 'Utilisateur modifié' });

					this.skipExitStateCheck = true;

					this.abonneService.refreshCurrentAbonneData().subscribe(() => {
						this.stateService.go('.', {uti_id: this.utilisateur.uti_id}, {reload: '^'});
					});
				}
			})
		}
	}

	resetPassword() {
		this.confirmationService.confirm({
				defaultFocus: 'reject',
				message: this.translationService.instant('auth.password_reinit_explanation'),
				acceptLabel: 'Envoyer',
				rejectLabel: 'Annuler',
				header: 'Confirmation',
				accept: () => {
					this.reinitialisationMotDePasseLoading = true;
						this.authService.reinitialisationMotDePasse(this.utilisateur.uti_email)
						.subscribe(
							(response: any) => {
								this.eventManager.emit('toast', {
									severity: 'success',
									summary: this.translationService.instant('auth.password_reinit_sending_success_summary'),
									detail:  this.translationService.instant('auth.password_reinit_sending_success_detail') + ' ' + this.utilisateur.uti_email,
								});
							}
						)
						.add(() => { this.reinitialisationMotDePasseLoading = false; });
					},
				}
		);
	}

	uiCanExit(newTransition?: Transition): HookResult {
		if (!this.skipExitStateCheck && !simpleComparison(this.pristineUtilisateur, this.utilisateur)) {
			return new Observable<any>(sub => {
				this.confirmationService.confirm({
					defaultFocus: 'reject',
					header: this.translationService.instant('common.leaving_unsaved_form_confirm_header'),
					message: this.translationService.instant('common.leaving_unsaved_form_confirm_message'),
					accept: () => {
						sub.next(true);
						sub.complete();
					},
					reject: () => {
						sub.next(false);
						sub.complete();
					}
				});
			}).toPromise();
		}
	}

}

@NgModule({
	imports: [
		CommonModule,
		GlobalModule,
		UIRouterModule,
		FormsModule,
		ToolbarModule,
		MenuModule,
		PanelModule,
		CiviliteSelectorModule,
		ToggleButtonModule,
		DividerModule,
		TooltipModule
	],
	exports: [UtilisateurGeneralTabComponent],
	declarations: [UtilisateurGeneralTabComponent]
})
export class UtilisateurGeneralTabModule { }
